import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { Fehler, Laden } from "./parts";

/**
 * Kündigungsweg im Betrieb (0415): Halte-Quote je Schritt, je Grund und je Angebot aus public.cancel_flows (nur lesend,
 * public.platform_cancel_flow_stats). Gehalten = behalten, pausiert oder Angebot angenommen.
 */
type Stats = {
  since: string; total: number; outcomes: Record<string, number>;
  steps: { step: number; reached: number; kept_here: number; left_here: number }[];
  reasons: { reason: string; n: number; kept: number }[];
  offers: { offer: string; shown: number; accepted: number }[];
};
const SCHRITT = ["", "Das verlierst du", "Pausieren (Test: Einrichtung)", "Grund", "Angebot", "Zusammenfassung"];
const GRUND: Record<string, string> = { zu_teuer: "Zu teuer", funktion_fehlt: "Eine Funktion fehlt", keine_zeit: "Keine Zeit", wenig_kooperationen: "Gerade wenig Kooperationen", anderes_werkzeug: "Anderes Werkzeug", sonstiges: "Sonstiges" };
const ANGEBOT: Record<string, string> = { pause: "Pause", rabatt: "50 % für 3 Monate", kleiner: "Kleinerer Tarif", jaehrlich: "Jährlich", funktion: "Fehlende Funktion (Ticket)", einrichtung: "Einrichtung durch uns", gespraech: "Gespräch", kleiner_naechstes_jahr: "Kleinerer Tarif zum nächsten Jahr" };
const ERGEBNIS: Record<string, string> = { offen: "offen", abgebrochen: "abgebrochen", behalten: "behalten", pausiert: "pausiert", angebot: "Angebot angenommen", gekuendigt: "gekündigt", loeschung_vorgemerkt: "Löschung vorgemerkt", test_beendet: "Test beendet" };
const quote = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)} %` : "–");

export function KuendigungswegBetrieb() {
  const [tage, setTage] = useState(90);
  const q = useQuery({ queryKey: ["platform_cancel_flow_stats", tage], queryFn: async () => {
    const r = await api.read<Stats | string>("platform_cancel_flow_stats", { p_days: tage });
    return (typeof r === "string" ? JSON.parse(r) : r) as Stats;
  } });
  const s = q.data;
  const gehalten = s ? (s.outcomes.behalten ?? 0) + (s.outcomes.pausiert ?? 0) + (s.outcomes.angebot ?? 0) : 0;
  return (
    <>
      <title>Kündigungsweg · Plattform · CreatorOffice</title>
      <h1>Kündigungsweg</h1>
      <p>Wie viele Agenturen im Kündigungsweg bleiben, je Schritt, Grund und Angebot. Gehalten heißt: behalten, pausiert oder ein Angebot angenommen.</p>
      <label className="row" style={{ gap: 8, alignItems: "center" }}>Zeitraum
        <select value={tage} onChange={(e) => setTage(Number(e.target.value))}>
          {[30, 90, 180, 365].map((n) => <option key={n} value={n}>{n} Tage</option>)}
        </select>
      </label>
      {q.isPending ? <Laden /> : q.error ? <Fehler error={q.error} /> : s && (
        <>
          <section className="panel stack" aria-labelledby="kw-gesamt">
            <h2 id="kw-gesamt">Gesamt</h2>
            <p>{s.total} Durchläufe, davon {gehalten} gehalten ({quote(gehalten, s.total)}).</p>
            <p>{Object.entries(s.outcomes).map(([k, n]) => `${ERGEBNIS[k] ?? k}: ${n}`).join(" · ") || "Noch keine Durchläufe."}</p>
          </section>
          <section className="panel stack" aria-labelledby="kw-schritte">
            <h2 id="kw-schritte">Je Schritt</h2>
            <div className="tablewrap"><table>
              <thead><tr><th>Schritt</th><th>Erreicht</th><th>Hier gehalten</th><th>Hier gekündigt</th><th>Halte-Quote</th></tr></thead>
              <tbody>{s.steps.map((x) => (
                <tr key={x.step}><td>{x.step} · {SCHRITT[x.step]}</td><td>{x.reached}</td><td>{x.kept_here}</td><td>{x.left_here}</td><td>{quote(x.kept_here, x.reached)}</td></tr>
              ))}</tbody>
            </table></div>
          </section>
          <section className="panel stack" aria-labelledby="kw-gruende">
            <h2 id="kw-gruende">Je Grund</h2>
            <div className="tablewrap"><table>
              <thead><tr><th>Grund</th><th>Anzahl</th><th>Gehalten</th><th>Quote</th></tr></thead>
              <tbody>{s.reasons.map((x) => <tr key={x.reason}><td>{GRUND[x.reason] ?? x.reason}</td><td>{x.n}</td><td>{x.kept}</td><td>{quote(x.kept, x.n)}</td></tr>)}</tbody>
            </table></div>
          </section>
          <section className="panel stack" aria-labelledby="kw-angebote">
            <h2 id="kw-angebote">Je Angebot</h2>
            <div className="tablewrap"><table>
              <thead><tr><th>Angebot</th><th>Gezeigt</th><th>Angenommen</th><th>Quote</th></tr></thead>
              <tbody>{s.offers.map((x) => <tr key={x.offer}><td>{ANGEBOT[x.offer] ?? x.offer}</td><td>{x.shown}</td><td>{x.accepted}</td><td>{quote(x.accepted, x.shown)}</td></tr>)}</tbody>
            </table></div>
          </section>
        </>
      )}
    </>
  );
}
